import React, { useState } from 'react';

import { Alert, Box, Button, LinearProgress, Stack, TextField, Typography } from '@mui/material';

import {
  useDryRunReportingLeLensDataset,
  useExtendCheckReportingLeLensDataset,
} from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type { LensCard, LensDataset, LensDatasetColumn, LensDatasetOverride } from '~/types/leLens';

import { bestDatasetSql, describeRejection } from '../../buildPlan';

import { extendColumnPrompt, extractCannotLine, extractGrainLine } from './extendPrompt';
import { type GuardOutcome, guardOutcome, passSummaryLine } from './guardCopy';
import { streamDatasetSqlAnswer } from './streamAiSql';

/**
 * F4, "+ Add column": one text input, "Ask askRichard" rewrites the
 * dataset's SQL to keep every column and add the requested one, then the
 * extend guard decides pass or refuse exactly as 02_RIGHT_PANEL_SPEC.md
 * specifies. Reuses the AskRichard streaming call `LensCardDialog.tsx`'s
 * "What should change?" already makes (see `streamAiSql.ts`'s header for
 * why this is a duplicate of the stream glue only, not of any parsing).
 */

type Stage =
  | { kind: 'idle' }
  | { kind: 'asking' }
  | { kind: 'checking'; candidateSql: string }
  | { kind: 'askFailed'; message: string }
  | {
      kind: 'result';
      outcome: GuardOutcome;
      candidateSql: string;
      /** The rewritten query's own typed columns, from the free dry run: the correct new
       *  `dataset.columns` for Apply/Apply anyway alike, kept and dropped columns included. */
      columns: Array<LensDatasetColumn>;
      grainKeys: Array<string>;
      dryRun: { at: string; bytesProcessed: number; referencedTables: Array<string> };
    };

export type AddColumnProps = {
  lensId: string;
  dataset: LensDataset;
  /** Cards that read this dataset, for the guard's "N cards re-run"/"N cards would change". */
  cards: ReadonlyArray<LensCard>;
  /** `access.canEdit`, which svc-reporting computes as `ownerEmail === current user`: the same
   *  check D7's "Lens owner" means, confirmed against checksController.ts. The guard itself
   *  403s for anyone else, so this hides the whole action rather than a broken button. */
  canEdit: boolean;
  /** Persists the rewritten dataset through the existing lens update mutation (the mount site
   *  holds the full Lens and its version; this component only ever hands it the next dataset,
   *  `override` folded in already for Apply anyway). */
  onApply: (nextDataset: LensDataset) => Promise<void>;
  /** F4 refusal's primary action: hand the candidate SQL to the New dataset composer instead of
   *  applying a grain-changing rewrite. */
  onNewDatasetInstead: (candidateSql: string) => void;
  /** D7: only a Builder (the dashboard owner) may override a grain refusal. Same value as `canEdit`
   *  today (no separate Editor role exists at the permission layer yet); kept as its own prop so a
   *  future narrower role does not have to change this component. */
  isOwner: boolean;
  /** `lens.ownerEmail`: who an "Apply anyway" override is recorded against. The caller already
   *  holds this to compute `isOwner`, so it is threaded through rather than re-derived here. */
  ownerEmail: string;
};

export default function AddColumn({
  canEdit,
  cards,
  dataset,
  isOwner,
  lensId,
  onApply,
  onNewDatasetInstead,
  ownerEmail,
}: AddColumnProps) {
  const [request, setRequest] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | undefined>(undefined);
  const dryRun = useDryRunReportingLeLensDataset();
  const extendCheck = useExtendCheckReportingLeLensDataset();

  if (!canEdit) {
    return (
      <Alert severity="info" variant="outlined">
        Only this Lens&rsquo;s owner can add a column.
      </Alert>
    );
  }

  const runAsk = async () => {
    if (!request.trim()) {
      return;
    }
    setStage({ kind: 'asking' });
    const columns = (dataset.columns ?? []).map((column) => column.name);
    const { answer, sqlBlocks, streamError } = await streamDatasetSqlAnswer(
      extendColumnPrompt({ columns, name: dataset.name, sql: dataset.sql }, request.trim()),
    );
    if (streamError) {
      setStage({ kind: 'askFailed', message: streamError });
      return;
    }
    const cannot = extractCannotLine(answer);
    const candidateSql = bestDatasetSql(sqlBlocks, columns);
    if (!candidateSql) {
      setStage({ kind: 'askFailed', message: cannot ?? describeRejection(sqlBlocks, columns) });
      return;
    }
    const grainKeys = extractGrainLine(answer) ?? dataset.grain?.keys ?? [];

    setStage({ candidateSql, kind: 'checking' });
    let dryRunResult: { columns: Array<LensDatasetColumn>; bytesProcessed: number; referencedTables: Array<string> };
    try {
      dryRunResult = await dryRun.mutateAsync(candidateSql);
    } catch (error) {
      setStage({ kind: 'askFailed', message: error instanceof Error ? error.message : String(error) });
      return;
    }

    try {
      const result = await extendCheck.mutateAsync({
        datasetId: dataset.id,
        grain: grainKeys,
        lensId,
        sql: candidateSql,
      });
      setStage({
        candidateSql,
        columns: dryRunResult.columns,
        dryRun: {
          at: new Date().toISOString(),
          bytesProcessed: dryRunResult.bytesProcessed,
          referencedTables: dryRunResult.referencedTables,
        },
        grainKeys,
        kind: 'result',
        outcome: guardOutcome(result, dryRunResult.columns, dataset, cards),
      });
    } catch (error) {
      setStage({ kind: 'askFailed', message: error instanceof Error ? error.message : String(error) });
    }
  };

  const discard = () => {
    setStage({ kind: 'idle' });
    setRequest('');
    setApplyError(undefined);
  };

  const apply = async (override?: LensDatasetOverride) => {
    if (stage.kind !== 'result') {
      return;
    }
    setApplying(true);
    setApplyError(undefined);
    try {
      await onApply({
        ...dataset,
        columns: stage.columns,
        dryRun: stage.dryRun,
        grain: { keys: stage.grainKeys },
        sql: stage.candidateSql,
        ...(override ? { override } : {}),
      });
      discard();
    } catch (error) {
      // A save can still be refused server-side (a card names a column this rewrite no longer
      // returns; the backend's own schema validation catches that even when a reader forced
      // Apply anyway). Shown here, in the result block, rather than left to escape as an
      // unhandled rejection: unhandled, it surfaced as a dev-error overlay in practice.
      setApplyError(error instanceof Error ? error.message : String(error));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Stack spacing={1.25}>
      <Typography sx={{ fontWeight: 500 }} variant="body2">
        What should {dataset.name} also return?
      </Typography>
      <TextField
        disabled={stage.kind === 'asking' || stage.kind === 'checking'}
        fullWidth
        onChange={(event) => setRequest(event.target.value)}
        placeholder="e.g. refunds in AUD"
        size="small"
        value={request}
      />
      <Box>
        <Button
          disabled={!request.trim() || stage.kind === 'asking' || stage.kind === 'checking'}
          onClick={runAsk}
          variant="contained"
        >
          Ask askRichard
        </Button>
      </Box>

      {(stage.kind === 'asking' || stage.kind === 'checking') && (
        <Stack spacing={0.5}>
          <Typography color="text.secondary" variant="caption">
            {stage.kind === 'asking'
              ? 'Asking askRichard to rewrite the query…'
              : `Dry run, then re-running ${cards.length} card${cards.length === 1 ? '' : 's'}…`}
          </Typography>
          <LinearProgress />
        </Stack>
      )}

      {stage.kind === 'askFailed' && <Alert severity="warning">{stage.message}</Alert>}

      {stage.kind === 'result' && stage.outcome.kind === 'pass' && (
        <Alert
          action={
            <Stack direction="row" spacing={1}>
              <Button disabled={applying} onClick={discard} size="small">
                Discard
              </Button>
              <Button disabled={applying} onClick={() => apply()} size="small" variant="contained">
                Apply
              </Button>
            </Stack>
          }
          severity="success"
        >
          {passSummaryLine(stage.outcome)} {stage.outcome.bytesLine}.
        </Alert>
      )}

      {stage.kind === 'result' && stage.outcome.kind === 'refuse' && (
        <Alert severity="warning">
          <Typography sx={{ fontWeight: 500 }} variant="body2">
            {stage.outcome.reasonLine} {stage.outcome.changedCards.length} card
            {stage.outcome.changedCards.length === 1 ? '' : 's'} would change:
          </Typography>
          <Box component="table" sx={{ borderCollapse: 'collapse', mt: 0.75, width: '100%' }}>
            <tbody>
              {stage.outcome.changedCards.map((changed) => (
                <tr key={changed.cardId}>
                  <Box component="td" sx={{ fontSize: 13, py: 0.25 }}>
                    {changed.title}
                  </Box>
                  <Box component="td" sx={{ fontFamily: 'monospace', fontSize: 12.5, py: 0.25, textAlign: 'right' }}>
                    {changed.display}
                  </Box>
                </tr>
              ))}
            </tbody>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button onClick={() => onNewDatasetInstead(stage.candidateSql)} size="small" variant="contained">
              Create a new dataset for this instead
            </Button>
            {isOwner && stage.outcome.canApplyAnyway && (
              <Button
                disabled={applying}
                onClick={() => apply({ at: new Date().toISOString(), overriddenBy: ownerEmail })}
                size="small"
              >
                Apply anyway
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      {applyError && <Alert severity="error">{applyError}</Alert>}
    </Stack>
  );
}
